import { useEffect } from 'react';
import Modal from 'react-modal';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { updateQrConfiguration } from 'api/qr';
import { QrConfiguration } from './types';
import swal from 'sweetalert';

Modal.setAppElement('#root');

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Nombre requerido'),
    target_url: Yup.string().url('URL inválida').required('URL destino requerida'),
    description: Yup.string(),
    is_active: Yup.boolean(),
});

interface Props {
    isOpen: boolean;
    config: QrConfiguration | null;
    onClose: () => void;
    onUpdated: () => void;
}

const QrEditModal = ({ isOpen, config, onClose, onUpdated }: Props) => {
    const formik = useFormik({
        initialValues: { name: '', target_url: '', description: '', is_active: true },
        validationSchema,
        onSubmit: async (values) => {
            if (!config) return;
            try {
                await updateQrConfiguration(config.id, values);
                onUpdated();
                onClose();
            } catch (err: any) {
                const msg = err?.response?.data?.target_url?.[0]
                    || err?.response?.data?.name?.[0]
                    || 'Error al actualizar la configuración QR';
                swal('Afinidata', msg, 'error');
            }
        },
        enableReinitialize: true,
    });

    useEffect(() => {
        if (config) {
            formik.setValues({
                name: config.name,
                target_url: config.target_url,
                description: config.description || '',
                is_active: config.is_active,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} contentLabel="Editar QR"
            style={{
                overlay: { backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 },
                content: { position: 'relative', inset: 'auto', maxWidth: 520, width: '100%', borderRadius: 8, padding: 0 }
            }}>
            <div className="card mb-0">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Editar configuración QR</h5>
                    <button className="btn btn-sm btn-light" onClick={onClose}>&times;</button>
                </div>
                <div className="card-body">
                    <div className="form-group">
                        <label>Slug</label>
                        <input type="text" className="form-control" value={config?.slug || ''} readOnly disabled />
                        <small className="form-text text-muted">El slug no puede modificarse.</small>
                    </div>
                    <form onSubmit={formik.handleSubmit}>
                        {(['name', 'target_url'] as const).map(name => (
                            <div className="form-group" key={name}>
                                <label>{name === 'name' ? 'Nombre' : 'URL destino'}</label>
                                <input
                                    type={name === 'target_url' ? 'url' : 'text'}
                                    name={name}
                                    className={`form-control ${formik.errors[name] && formik.touched[name] ? 'is-invalid' : ''}`}
                                    value={formik.values[name]}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                {formik.errors[name] && formik.touched[name] && (
                                    <div className="invalid-feedback">{formik.errors[name]}</div>
                                )}
                            </div>
                        ))}
                        <div className="form-group">
                            <label>Descripción</label>
                            <textarea
                                name="description"
                                className="form-control"
                                rows={2}
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </div>
                        <div className="form-group form-check">
                            <input type="checkbox" className="form-check-input" id="is_active_edit"
                                name="is_active" checked={formik.values.is_active} onChange={formik.handleChange} />
                            <label className="form-check-label" htmlFor="is_active_edit">Activo</label>
                        </div>
                        <div className="d-flex justify-content-end">
                            <button type="button" className="btn btn-secondary mr-2" onClick={onClose}>Cancelar</button>
                            <button type="submit" className="btn btn-primary" disabled={formik.isSubmitting}>
                                {formik.isSubmitting ? 'Guardando...' : 'Guardar cambios'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    );
};

export default QrEditModal;
